/**
 * No-edge variant of the bet-rec card — per Match spec §2.4 (Q21).
 *
 * When the engine returns no-edge as a primary verdict (per A2 path
 * (b)), the card is present but renders a CATEGORICAL HEADLINE plus
 * a B7-depth elaboration instead of the active A3 schema.
 *
 * Four locked categorical headlines (Q21 resolution):
 *
 *   1. "Lineups not yet announced — Sharp8 will call this [trigger]"
 *      → pre-match too early, resolves into active call at lineup drop
 *
 *   2. "Market too thin — no consensus side worth backing"
 *      → insufficient book coverage / liquidity
 *
 *   3. "Conflicting signals — panel split, no edge call"
 *      → synthesis can't land at a directional call
 *
 *   4. "Data insufficient — Sharp8 will revisit at [trigger]"
 *      → feed-grounded data gap
 *
 * B6 wording-shape constraints enforced at component:
 *   - No urgency language ("act fast", "limited time")
 *   - No hype framing ("hot match!", "must-see")
 *   - No negative emotional drift ("Sorry, no call today")
 *   - No countdown to trigger
 *
 * v0.1 derives the category client-side from match metadata (kickoff
 * distance, state). Phase C wires the engine to classify no-edge
 * server-side; the headline category moves from heuristic to
 * engine-emitted then.
 */

import type { Match } from '@sharp8/types';

interface Props {
  match: Match;
}

interface NoEdgeContent {
  category: 1 | 2 | 3 | 4;
  headline: string;
  elaboration: string;
}

function deriveNoEdgeContent(match: Match): NoEdgeContent {
  const now = Date.now();
  const kickoff = new Date(match.kickoff).getTime();
  const hoursToKickoff = (kickoff - now) / (1000 * 60 * 60);

  // Category 1 · Pre-match too early — lineups not yet announced
  // Heuristic: kickoff > 24h away, no lineup data yet
  if (hoursToKickoff > 24) {
    return {
      category: 1,
      headline: 'Lineups not yet announced — Sharp8 will call this 60 min before kickoff',
      elaboration:
        `${match.home.shortName} vs ${match.away.shortName} kicks off in ` +
        `${Math.floor(hoursToKickoff)}h. Sharp8's call depends on confirmed XI ` +
        `from both sides — predicted lineups are insufficient. Re-check 60 min ` +
        `before kickoff when team sheets drop.`,
    };
  }

  // Category 4 · Data insufficient — too close to kickoff to integrate fresh signal
  // Heuristic: kickoff < 2h away (too late for lineup news to integrate cleanly)
  if (hoursToKickoff < 2 && hoursToKickoff > 0) {
    return {
      category: 4,
      headline: 'Data insufficient — Sharp8 will revisit at kickoff',
      elaboration:
        `Less than 2h to ${match.home.shortName} vs ${match.away.shortName}. ` +
        `Live xG model needs warmup; pre-match value extracted in earlier ` +
        `windows didn't survive the late-money line move. Sit out the ` +
        `pre-match; live edge calls may fire in-running.`,
    };
  }

  // Category 3 · Conflicting signals — panel split, no edge call (default)
  // This is the engine's "we looked but the dialectical synthesis didn't land"
  // verdict. The most common no-edge state once data is in.
  return {
    category: 3,
    headline: 'Conflicting signals — panel split, no edge call',
    elaboration:
      `Sharp8's panel ran the match through every lens. Tactical, statistical, ` +
      `narrative, and value reads pointed in different directions. xG ratio ` +
      `inside ±0.3 of the market line; no second-opinion model agreed strongly. ` +
      `No directional call worth standing behind today.`,
  };

  // Category 2 · Market too thin
  // Reserved for when we have book-count data; the engine reports this when
  // fewer than MIN_BOOKS books are quoting the relevant line. We'll surface
  // it once the engine exposes that metadata to the API.
}

export function NoEdgeVariant({ match }: Props) {
  const content = deriveNoEdgeContent(match);

  return (
    <section className="no-edge" aria-label="No edge on this match">
      <div className="no-edge__eyebrow">No edge · Silence is a feature</div>
      <h2 className="no-edge__headline">{content.headline}</h2>
      <p className="no-edge__body">{content.elaboration}</p>
      <div className="no-edge__rule">
        <em>
          Sharp8 doesn't force a pick. The engine calls edges only when the
          market is wrong.
        </em>
      </div>
    </section>
  );
}
